// frontend\src\pages\AboutPage\ImageWithFallback.js
import { useState, useRef, useEffect } from "react";

const ImageWithFallback = ({ src, alt, imageBasePath, onImageClick }) => {
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [isOriginalSize, setIsOriginalSize] = useState(false);
  const isMountedRef = useRef(true);
  const imgRef = useRef(null);
  const imageCacheRef = useRef(new Map()); // Кеш для уже загруженных изображений

  console.log('ImageWithFallback MOUNTED:', { src, imageBasePath });

  // Очистка при размонтировании
  useEffect(() => {
    const currentImgRef = imgRef.current;
    
    return () => {
      console.log('ImageWithFallback UNMOUNTING:', { src, imageBasePath });
      isMountedRef.current = false;
      if (currentImgRef) {
        currentImgRef.onload = null;
        currentImgRef.onerror = null;
        currentImgRef.src = '';
      }
    };
  }, [src, imageBasePath]);

  // Сброс состояния при изменении props
  useEffect(() => {
    if (!isMountedRef.current) return;
    
    console.log('ImageWithFallback RESETTING STATE for new props:', { src, imageBasePath });
    
    const imageUrl = `${imageBasePath}${src}`;
    const cachedImage = imageCacheRef.current.get(imageUrl);
    
    if (cachedImage && cachedImage.complete) {
      // Изображение уже загружено в кеше
      console.log('ImageWithFallback: using cached image', imageUrl);
      setImageDimensions({
        width: cachedImage.naturalWidth,
        height: cachedImage.naturalHeight
      });
      const isSmall = cachedImage.naturalWidth <= 600 && cachedImage.naturalHeight <= 600;
      setIsOriginalSize(isSmall);
      setLoading(false);
      setHasError(false);
    } else {
      // Нужно загружать изображение
      setHasError(false);
      setLoading(true);
      setImageDimensions({ width: 0, height: 0 });
      setIsOriginalSize(false);
    }
  }, [src, imageBasePath]);

  const imageUrl = `${imageBasePath}${src}`;

  const handleLoad = (e) => {
    if (!isMountedRef.current) {
      console.log('ImageWithFallback: skipping load - component unmounted');
      return;
    }

    const img = e.target;
    console.log('ImageWithFallback img onLoad:', imageUrl, {
      width: img.naturalWidth,
      height: img.naturalHeight
    });

    // Сохраняем в кеш
    imageCacheRef.current.set(imageUrl, img);

    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    });

    const isSmall = img.naturalWidth <= 600 && img.naturalHeight <= 600;
    setIsOriginalSize(isSmall);
    setLoading(false);
    setHasError(false);

    console.log('ImageWithFallback isOriginalSize:', isSmall);
  };

  const handleError = () => {
    if (!isMountedRef.current) {
      console.log('ImageWithFallback: skipping error - component unmounted');
      return;
    }

    console.error('ImageWithFallback img onError:', imageUrl);
    setHasError(true);
    setLoading(false);
  };

  const handleImageClick = () => {
    if (!hasError && !loading && !isOriginalSize) {
      console.log('Opening image modal:', imageUrl);
      onImageClick({
        src: imageUrl,
        alt: alt,
        dimensions: imageDimensions
      });
    }
  };

  if (hasError) {
    console.log('ImageWithFallback RENDERING NULL due to error:', src);
    return null;
  }

  console.log('ImageWithFallback RENDERING:', { 
    src, 
    loading, 
    hasError, 
    isOriginalSize,
    imageUrl 
  });

  return (
    <div className="text-center my-4">
      {loading && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка изображения...</span>
          </div>
        </div>
      )}
      <div 
        className="image-clickable-container position-relative d-inline-block"
        style={{ 
          cursor: loading || isOriginalSize ? 'default' : 'pointer',
        }}
        onClick={handleImageClick}
      >
        <img 
          ref={imgRef}
          src={imageUrl}
          alt={alt || ''} 
          className="img-fluid rounded shadow-sm mx-auto d-block"
          style={{ 
            maxHeight: '400px', 
            objectFit: 'contain',
            display: loading ? 'none' : 'block',
            transition: 'transform 0.2s ease-in-out',
            backgroundColor: '#ffffff',
            border: 'none',
            borderRadius: '8px'
          }}
          onError={handleError}
          onLoad={handleLoad}
          onMouseEnter={(e) => {
            if (!loading && !isOriginalSize) {
              e.target.style.transform = 'scale(1.03)';
              e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              const zoomIndicator = e.target.parentElement.querySelector('.zoom-indicator');
              if (zoomIndicator) {
                zoomIndicator.style.opacity = '1';
              }
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && !isOriginalSize) {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              const zoomIndicator = e.target.parentElement.querySelector('.zoom-indicator');
              if (zoomIndicator) {
                zoomIndicator.style.opacity = '0';
              }
            }
          }}
        />
        
        {!loading && !isOriginalSize && (
          <div 
            className="zoom-indicator position-absolute top-50 start-50 translate-middle"
            style={{
              width: '60px',
              height: '60px',
              backgroundColor: 'rgba(0, 123, 255, 0.9)',
              color: 'white',
              fontSize: '24px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'all 0.3s ease-in-out',
              pointerEvents: 'none',
              boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
              border: '3px solid white'
            }}
          >
            <i className="bi bi-zoom-in"></i>
          </div>
        )}
      </div>
      
      {alt && !loading && (
        <div className="image-caption mt-2">
          <div className="text-muted small">
            {alt}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageWithFallback;